import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { NOTIFICATIONS_REFRESH_EVENT } from '../../utils/notificationEvents';
import { PROFILE_PHOTO_UPDATED_EVENT } from '../../utils/profileEvents';
import { Link, useLocation, useNavigate } from 'react-router';
import '../../styles/navbar.css';
import '../../styles/notifications.css';
import axios from 'axios';
import { toast } from 'react-toast';
import { FaBell ,FaCircle} from 'react-icons/fa';
import { HiMiniCircle } from "react-icons/hi2";
import useNotificationSocket from '../../hooks/useNotificationSocket';
import formatTimestamp from '../../utils/formatTimestamp';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { INTENT_TYPES, STEP_STATUS } from '../../Config/Onboarding/constant';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { useWorkspaceList, useUpdateWorkspace } from '../../hooks/useWorkspace';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectTypeBadge } from '../common';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const isUserAuthenticated = () => localStorage.getItem('isAuthenticated') === 'true';

const SOCKET_SYNC_MIN_INTERVAL_MS = 5000;

const isNotificationRead = (notification) =>
  notification?.read === true ||
  notification?.is_read === true ||
  notification?.status === 'read';

const resolveUnreadNotifications = (data) => {
  const notifications = Array.isArray(data?.notifications) ? data.notifications : [];

  if (typeof data?.unread_count === 'number') {
    return { notifications, count: data.unread_count };
  }

  if (typeof data?.unreadCount === 'number') {
    return { notifications, count: data.unreadCount };
  }

  const unread = notifications.filter((notification) => !isNotificationRead(notification));
  return { notifications: unread, count: unread.length };
};

const Navbar = ({ user, onLogout, activeLink, onProjectCreated, searchTerm, onSearchChange, fetchProjects, currentWorkspace: parentCurrentWorkspace }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedProject, selectProject, projects, updateProjects, setLoading: setContextLoading, setError: setContextError, clearProject } = useProject();
  const { setUserWorkspaces, currentWorkspace, clearWorkspace} = useWorkspace();
  const { resetOnboarding, initializeStepFlow, markStepCompleted, setStepStatus, nextStep: moveNextStep } = useOnboarding();
  const { navigateToWorkspaceRoute, isWorkspaceRoute, getCleanPath, getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectType, setProjectType] = useState('');
  const [projectStatus, setProjectStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  
  // Project dropdown states
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  const notificationRef = useRef(null);
  const notificationsFetchRequestIdRef = useRef(0);
  const initialNotificationsLoadedRef = useRef(false);
  const lastSocketSyncAtRef = useRef(0);
  const projectDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const workspaceDropdownRef = useRef(null);
  const projectsFetchAbortRef = useRef(null);

  // Workspace data
  const [isLoggingOut, setIsLoggingOut] = useState(
    () => sessionStorage.getItem('isLoggingOut') === 'true'
  );
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('isAuthenticated') === 'true'
  );
  // const { data: workspacesData = {} } = useWorkspaceList({

  const shouldSkipAuthenticatedRequests = () =>
    isLoggingOut ||
    !isAuthenticated ||
    sessionStorage.getItem('isLoggingOut') === 'true' ||
    !isUserAuthenticated();

  const { data: workspacesData = {}, isLoading: workspacesLoading } = useWorkspaceList({
    enabled: isAuthenticated && !isLoggingOut
  });  
  const { mutate:updateWorkspaceFn, isLoading: updateWorkspaceLoading } = useUpdateWorkspace()
  const workspaces = workspacesData?.data || [];

  // Get userId from user prop or localStorage
  const userId = user?.userId || user?.id || (user ? JSON.parse(localStorage.getItem('user') || '{}').userId : null);
  const { latestNotification, status: notificationStatus, isConnected, markAllAsRead: markAllNotificationsAsRead, disconnect } = useNotificationSocket(userId);

  // Sync workspaces to context for Dashboard validation
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      setUserWorkspaces(workspaces);
    }
  }, [workspaces, setUserWorkspaces]);

  // Compute workspace prefix and current active segment
  const projectIdFromUrl = getProjectIdFromURL() || selectedProject?.id;
  const workspacePrefix = currentWorkspace?.id
    ? buildWorkspaceDashboardPath(currentWorkspace.id, projectIdFromUrl)
    : '/dashboard';
  const currentSegment = isWorkspaceRoute() ? (getCleanPath() || '').split('/')[0] : (location.pathname || '').replace(/^\/dashboard\/?/, '').split('/')[0] || '';

  useEffect(() => {
    // console.log("[Navbar] Notification socket status:", notificationStatus);
    // console.log("[Navbar] Notification socket connected:", isConnected?.());
  }, [notificationStatus, isConnected]);

  const applyNotificationPayload = useCallback((data) => {
    const { notifications, count } = resolveUnreadNotifications(data);
    setUnreadNotifications(notifications);
    setUnreadNotificationCount(count);
    initialNotificationsLoadedRef.current = true;
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (shouldSkipAuthenticatedRequests()) return;

    const requestId = ++notificationsFetchRequestIdRef.current;

    try {
      const response = await axiosInstance.get('/user_profile/list_user_notifications', {
        skipGlobalLoader: true,
      });
      if (requestId !== notificationsFetchRequestIdRef.current) return;

      if (response.status === 200) {
        applyNotificationPayload(response.data);
      } else {
        setUnreadNotifications([]);
        setUnreadNotificationCount(0);
        initialNotificationsLoadedRef.current = true;
      }
    } catch (error) {
      if (requestId !== notificationsFetchRequestIdRef.current) return;
      console.error('Error fetching notifications:', error);
      if (error?.response?.status !== 401) {
        toast.error('Failed to fetch notifications');
      }
    }
  }, [applyNotificationPayload]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handleRefresh = () => {
      setTimeout(() => fetchNotifications(), 400);
    };
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
  }, [fetchNotifications]);

  // Re-sync from API after real-time events, but only once the initial load has finished.
  // Throttled so a burst of socket messages can't hit the API more than once per interval;
  // the latest event still gets a trailing sync.
  useEffect(() => {
    if (!latestNotification || !initialNotificationsLoadedRef.current) return;

    const elapsed = Date.now() - lastSocketSyncAtRef.current;
    const delay = Math.max(500, SOCKET_SYNC_MIN_INTERVAL_MS - elapsed);
    const syncTimer = setTimeout(() => {
      lastSocketSyncAtRef.current = Date.now();
      fetchNotifications();
    }, delay);
    return () => clearTimeout(syncTimer);
  }, [latestNotification, fetchNotifications]);

const handleLogout = () => {
  sessionStorage.setItem('isLoggingOut', 'true');
  setIsLoggingOut(true);
  setIsAuthenticated(false);
  projectsFetchAbortRef.current?.abort();
  projectsFetchAbortRef.current = null;
  if (disconnect) disconnect();

  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');
  localStorage.removeItem('selectedWorkspace');
  localStorage.removeItem('selectedProject');

  queryClient.cancelQueries();
  queryClient.removeQueries();

  clearWorkspace();
  selectProject("", "");
  if (onLogout) onLogout();
  navigate('/login');
};

  const handleNotificationClick = async (notification) => {
    try {
      await axiosInstance.put(
        `/user_profile/mark_as_read_notifications?notification_id=${notification.id}`,
        null,
        { skipGlobalLoader: true }
      );
      setUnreadNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
      toast.success('Notification marked as read');
      setNotificationsOpen(false);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleRemoveNotification = async (index, e) => {
    e.stopPropagation();
    const notification = unreadNotifications[index];
    try {
      await axiosInstance.put(
        `/user_profile/mark_as_read_notifications?notification_id=${notification.id}`,
        null,
        { skipGlobalLoader: true }
      );
      setUnreadNotifications((prev) => prev.filter((_, i) => i !== index));
      setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
       setUnreadNotifications([]);
       setUnreadNotificationCount(0);
      setNotificationsOpen(false);

       await axiosInstance.put(`/user_profile/mark_as_read_notifications`, null, {
        skipGlobalLoader: true,
      });
      if (markAllNotificationsAsRead) {
        markAllNotificationsAsRead();
      }
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
      const response = await axiosInstance.get('/user_profile/list_user_notifications', {
        skipGlobalLoader: true,
      });
      if (response.status === 200) {
        applyNotificationPayload(response.data);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchProfilePhoto = async () => {
      if (shouldSkipAuthenticatedRequests()) return;

      try {
        const response = await axiosInstance.get('/user_profile/profile_detail');
        if (isMounted) {
          setProfilePhoto(response?.profile?.photo || null);
        }
      } catch (error) {
        console.error('Error fetching profile photo:', error);
      }
    };

    fetchProfilePhoto();

    const handleProfilePhotoUpdated = (event) => {
      if (!isMounted) return;
      if (event?.detail && Object.prototype.hasOwnProperty.call(event.detail, 'photo')) {
        setProfilePhoto(event.detail.photo || null);
        return;
      }
      fetchProfilePhoto();
    };

    window.addEventListener(PROFILE_PHOTO_UPDATED_EVENT, handleProfilePhotoUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(PROFILE_PHOTO_UPDATED_EVENT, handleProfilePhotoUpdated);
    };
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (currentSegment !== 'projects') return;

    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest('.navbar-mobile-toggle')) {
        setMobileMenuOpen(false);
      }

      if (userDropdownOpen && userDropdownRef.current && !userDropdownRef.current.contains(event.target) &&
        !event.target.closest('.navbar-user')) {
        setUserDropdownOpen(false);
      }

      if (
        notificationsOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, userDropdownOpen, notificationsOpen]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    // Determine where to send the search based on current segment
    const searchMap = {
      '': 'projects',
      'projects': 'projects',
      'test-plans': 'test-plans',
      'test-suites': 'test-suites',
      'test-cases': 'test-cases',
      'reports': 'reports',
      'active-projects': 'active-projects'
    };

    const targetSegment = searchMap[currentSegment] || 'projects';
    const targetPath = `${workspacePrefix}/${targetSegment}`;
    navigate(targetPath, {
      state: { searchTerm },
      replace: currentSegment === targetSegment
    });
  };

  const handleCreateProject = async () => {
    const authDataString = localStorage.getItem('user');
    if (!authDataString) {
      throw new Error('User not authenticated - no auth data found');
    }

    const authData = JSON.parse(authDataString);
    const accessToken = authData.accessToken;
    if (!accessToken) {
      throw new Error('Access token not found in auth data');
    }

    const projectData = {
      project_name: projectName,
      description: projectDescription,
      project_type: projectType,
      status: projectStatus,
    };

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/projects/add_project`, projectData, {
        headers: {
          'token': `${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (onProjectCreated) {
        const newProject = {
          project_id: response.data.project_id || Date.now(),
          project_name: projectName,
          description: projectDescription,
          project_type: projectType,
          status: projectStatus,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        onProjectCreated(newProject);
      }

      setProjectName('');
      setProjectDescription('');
      setProjectType('');
      setProjectStatus('active');
      toggleCreateProjectModal();
      toast.success('Project created successfully!');
      if (fetchProjects) {
        await fetchProjects();
      }
    } catch (error) {
      console.error('Error creating project:', error.response?.data || error.message);
      toast.error('Failed to create project', { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (userDropdownOpen) setUserDropdownOpen(false);
  };

  const toggleCreateProjectModal = () => {
    setCreateProjectModalOpen(!createProjectModalOpen);
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };

  // Start a fresh wizard run directly at Step 2 (workspace creation); the Link navigates afterwards
  const handleGettingStarted = () => {
    setUserDropdownOpen(false);
    const intent = localStorage.getItem('wizard_intent') || INTENT_TYPES.JIRA_REQUIREMENTS;
    resetOnboarding();
    initializeStepFlow(intent);
    markStepCompleted(1);
    moveNextStep();
    setStepStatus(2, STEP_STATUS.IN_PROGRESS);
  };

  // Fetch projects for dropdown - accepts optional workspaceId parameter
  const fetchProjectsList = async (workspaceId = null) => {
    if (shouldSkipAuthenticatedRequests()) {
      return;
    }

    projectsFetchAbortRef.current?.abort();
    const abortController = new AbortController();
    projectsFetchAbortRef.current = abortController;

    try {
      setProjectsLoading(true);
      setProjectsError(null);
      
      // Use provided workspaceId, fallback to context, then try URL
      let finalWorkspaceId = workspaceId || currentWorkspace?.id || getWorkspaceIdFromURL();
      
      if (!finalWorkspaceId) {
        console.warn('No workspace ID available for projects fetch');
        setProjectsError('Unable to load projects - no workspace selected');
        return;
      }
      
      const response = await axiosInstance.get(
        '/projects/list_project',
        {
          params: {
            pro_status: 'active',
            workspace_id: finalWorkspaceId,
          },
          signal: abortController.signal,
        }
      );

      if (shouldSkipAuthenticatedRequests()) {
        return;
      }
      
      if (response?.projects) {
        setProjectsList(response.projects);
        updateProjects(response.projects);
      }
    } catch (error) {
      if (
        abortController.signal.aborted ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      if (shouldSkipAuthenticatedRequests()) {
        return;
      }
      console.error('Error fetching projects:', error);
      setProjectsError('Failed to load projects. Please try again.');
      toast.error('Failed to load projects');
    } finally {
      if (!abortController.signal.aborted && !shouldSkipAuthenticatedRequests()) {
        setProjectsLoading(false);
      }
    }
  };

  // Handle project selection from dropdown
  const handleProjectSelect = (project) => {
    // Convert project_id to string for consistent comparison
    selectProject(String(project.project_id), project.project_name);
    if (currentWorkspace?.id) {
      updateWorkspaceFn({
        last_active_workspace_id: currentWorkspace.id,
        last_active_project_id: project.project_id,
      });
    }
    setProjectDropdownOpen(false);
    setProjectSearchQuery('');
    toast.success(`Project "${project.project_name}" selected`);
  };

  // Filter projects based on search query
  const filteredProjects = projectsList.filter(project =>
    project.project_name.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );

  // Open dropdown and fetch projects if needed
  const handleProjectDropdownOpen = () => {
    if (!projectDropdownOpen && projectsList.length === 0 && !projectsLoading) {
      fetchProjectsList();
    }
    setProjectDropdownOpen(true);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setProjectDropdownOpen(false);
      }
    };

    if (projectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [projectDropdownOpen]);

  const activeProjectId = projectIdFromUrl;

  const displayProjectName = useMemo(() => {
    if (selectedProject?.name) {
      return selectedProject.name;
    }

    if (!activeProjectId) {
      return '';
    }

    const fromList = projectsList.find(
      (project) => String(project.project_id) === String(activeProjectId)
    );
    if (fromList?.project_name) {
      return fromList.project_name;
    }

    const fromContext = projects.find(
      (project) => String(project.project_id) === String(activeProjectId)
    );
    if (fromContext?.project_name) {
      return fromContext.project_name;
    }

    try {
      const storedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
      if (String(storedProject.id) === String(activeProjectId) && storedProject.name) {
        return storedProject.name;
      }
    } catch {
      // Ignore invalid localStorage data
    }

    return '';
  }, [selectedProject?.id, selectedProject?.name, activeProjectId, projectsList, projects]);

  useEffect(() => {
    if (shouldSkipAuthenticatedRequests()) {
      return;
    }

    if (!activeProjectId || selectedProject?.name) {
      return;
    }

    if (projectsList.length === 0 && !projectsLoading) {
      fetchProjectsList();
    }
  }, [activeProjectId, selectedProject?.name, projectsList.length, projectsLoading, isLoggingOut, isAuthenticated]);

  useEffect(() => {
    if (shouldSkipAuthenticatedRequests()) {
      return;
    }

    if (!activeProjectId || selectedProject?.name) {
      return;
    }

    const availableProjects = projectsList.length > 0 ? projectsList : projects;
    const matchedProject = availableProjects.find(
      (project) => String(project.project_id) === String(activeProjectId)
    );

    if (matchedProject?.project_name) {
      selectProject(String(activeProjectId), matchedProject.project_name);
    }
  }, [activeProjectId, selectedProject?.name, projectsList, projects, selectProject, isLoggingOut, isAuthenticated]);

const workspaceName = currentWorkspace?.workspace_name
  ?? JSON.parse(localStorage.getItem("selectedWorkspace") || "null")?.workspace_name;
const truncateLabel = (name = '', maxLength = 7) => (
  name.length > maxLength ? `${name.slice(0, maxLength)}...` : name
);
const truncatedWorkspaceName = truncateLabel(workspaceName || '');
const truncatedProjectName = truncateLabel(displayProjectName || '');
  
  return (
    <>
      {/* <ToastContainer /> */}
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <Link to={workspacePrefix} className="navbar-brand">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 215 215"
            fill="none"
            className="navbar-logo"
          >
            <path
              d="M115 10C115 4.47715 110.523 0 105 0C99.4772 0 95 4.47715 95 10V30C95 35.5228 99.4772 40 105 40C110.523 40 115 35.5228 115 30V10Z"
              fill="white"
            />
            <path
              d="M105 60C66.3401 60 35 91.3401 35 130V170C35 192.091 52.9086 210 75 210H135C157.091 210 175 192.091 175 170V130C175 91.3401 143.66 60 105 60Z"
              stroke="white"
              strokeWidth="20"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M75 130C75 130 75 120 75 115C75 110 80 105 85 105C90 105 95 110 95 115C95 120 95 130 95 130"
              stroke="white"
              strokeWidth="20"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M115 130C115 130 115 120 115 115C115 110 120 105 125 105C130 105 135 110 135 115C135 120 135 130 135 130"
              stroke="white"
              strokeWidth="20"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M75 170H135"
              stroke="white"
              strokeWidth="20"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {/* <h1 className="navbar-title">Robitest Web</h1> */}
        </Link>

        <button
          className="navbar-mobile-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>

        <div
          className={`navbar-menu ${mobileMenuOpen ? "open" : ""}`}
          ref={mobileMenuRef}
        >
          {mobileMenuOpen && (
            <button
              className="navbar-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}

          <ul className="navbar-links">
            <li>
              <Link
                to={workspacePrefix}
                className={`navbar-link ${currentSegment === "" || location.pathname === "/dashboard" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Dashboard
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/projects`}
                className={`navbar-link ${currentSegment === "projects" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Projects
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/test-plans`}
                className={`navbar-link ${currentSegment === "test-plans" ? "active" : ""}`}
                onClick={() =>
                  handleNavLinkClick(`${workspacePrefix}/test-plans`)
                }
              >
                Test Execution
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/test-suites`}
                className={`navbar-link ${currentSegment === "test-suites" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Test Suites
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/test-cases`}
                className={`navbar-link ${currentSegment === "test-cases" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Test Cases
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/reports`}
                className={`navbar-link ${currentSegment === "reports" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Reports
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/settings`}
                className={`navbar-link ${currentSegment === "settings" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                Settings
              </Link>
            </li>
            <li>
              <Link
                to={`${workspacePrefix}/learning`}
                className={`navbar-link ${currentSegment === "learning" ? "active" : ""}`}
                onClick={() => handleNavLinkClick}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                Feedback & Learning
              </Link>
            </li>
          </ul>

          {workspaceName && (
            <Link
              to="/dashboard/all-workspaces"
              title={`${workspaceName}${displayProjectName ? ` · ${displayProjectName}` : ''}`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 shrink-0 max-w-[400px] cursor-pointer hover:bg-white/15 transition-colors no-underline"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
              </span>
              <span className="text-white/50 text-xs font-medium whitespace-nowrap shrink-0">
                Workspace & Project
              </span>
              <span className="text-white text-xs font-semibold truncate min-w-0">
                {truncatedWorkspaceName} / {truncatedProjectName}
              </span>
            </Link>
          )}

          <div className="navbar-actions">
            {/* Selected project name (read-only) */}
            {/* <div className="navbar-project-selector">
              <div
                className="navbar-project-button navbar-project-readonly"
                title={displayProjectName || 'No project selected'}
                aria-label={`Current project: ${displayProjectName || 'No project selected'}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 21H3V4a1 1 0 0 1 1-1h4v16M9 3h11v13M9 16h11"></path>
                </svg>
                <span className="navbar-project-text">
                  {displayProjectName || 'No project selected'}
                </span>
              </div>
            </div> */}

            {/* Project Dropdown - commented out (project selection disabled in navbar)
            <div className="navbar-project-selector" ref={projectDropdownRef}>
              <button
                className="navbar-project-button"
                onClick={handleProjectDropdownOpen}
                title={selectedProject.name || "All Projects"}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 21H3V4a1 1 0 0 1 1-1h4v16M9 3h11v13M9 16h11"></path>
                </svg>
                <span className="navbar-project-text">
                  {selectedProject.name || "All Projects"}
                </span>
              </button>

              {projectDropdownOpen && (
                <div className="navbar-project-dropdown">
                  <div className="project-dropdown-header">
                    <input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      className="project-search-input"
                      autoFocus
                      style={{
                        color: "#333",
                        backgroundColor: "#fff",
                        padding: "8px 12px",
                        fontSize: "14px",
                      }}
                    />
                  </div>

                  <div className="project-dropdown-content">
                    {projectsLoading ? (
                      <div className="project-dropdown-loading">
                        <div className="loading-spinner"></div>
                        <span>Loading projects...</span>
                      </div>
                    ) : projectsError ? (
                      <div className="project-dropdown-error">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>{projectsError}</span>
                      </div>
                    ) : (
                      <>
                        <button
                          className={`project-dropdown-item ${selectedProject.id === "" ? "selected" : ""}`}
                          onClick={() => {
                            selectProject("", "");
                            setProjectDropdownOpen(false);
                            setProjectSearchQuery("");
                            toast.success("All Projects selected");
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M15 21H3V4a1 1 0 0 1 1-1h4v16M9 3h11v13M9 16h11"></path>
                          </svg>
                          <div className="project-item-content">
                            <div className="project-item-name">
                              All Projects
                            </div>
                          </div>
                          {selectedProject.id === "" && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </button>

                        {filteredProjects.length > 0 ? (
                          filteredProjects.map((project) => (
                            <button
                              key={project.project_id}
                              className={`project-dropdown-item ${selectedProject.id === String(project.project_id) ? "selected" : ""}`}
                              onClick={() => handleProjectSelect(project)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M15 21H3V4a1 1 0 0 1 1-1h4v16M9 3h11v13M9 16h11"></path>
                              </svg>
                              <div className="project-item-content">
                                <div className="project-item-name">
                                  {project.project_name}
                                </div>
                                <div className="project-item-type">
                                  <ProjectTypeBadge type={project.project_type} />
                                </div>
                              </div>
                              {selectedProject.id ===
                                String(project.project_id) && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="project-dropdown-empty">
                            <p>No projects found</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            */}

            {/* <button className="navbar-button primary create-project-btn" onClick={toggleCreateProjectModal}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="create-project-icon">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create Project
            </button> */}
            {/* {location.pathname !== '/dashboard' && (
              <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                <span className="navbar-search-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder={
                    location.pathname.startsWith('/dashboard/test-cases') ? 'Search test cases...' :
                      location.pathname.startsWith('/dashboard/test-plans') ? 'Search test plans...' :
                        location.pathname.startsWith('/dashboard/test-suites') ? 'Search test suites...' :
                          location.pathname.startsWith('/dashboard/reports') ? 'Search reports...' :
                            'Search projects...'
                  }
                  value={searchTerm}
                  onChange={onSearchChange}
                />
              </form>
            )} */}
            <div className="navbar-notification" ref={notificationRef}>
              <div
                className="notification-icon"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
              >
                <FaBell size={20} />
                {unreadNotificationCount > 0 && (
                  <span className="navbar-notification-badge">
                    {unreadNotificationCount}
                  </span>
                )}
              </div>

              {notificationsOpen && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <span className="notifications-title">
                      Notifications{" "}
                      {unreadNotificationCount > 0 &&
                        `(${unreadNotificationCount})`}
                    </span>
                    <div className="notifications-actions">
                      {unreadNotificationCount > 0 && (
                        <span className="mark-all-read" onClick={markAllAsRead}>
                          Mark all as read
                        </span>
                      )}
                      <button
                        className="notification-close-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotificationsOpen(false);
                        }}
                        aria-label="Close notifications"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="notifications-list">
                    {unreadNotifications.length > 0 ? (
                      unreadNotifications.map((notification, index) => (
                        <div
                          key={notification.id || index}
                          className="notification-item unread"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="notification-dot" />
                          <div className="notification-content">
                            <div className="notification-message">
                              {notification.message}
                            </div>
                            <div className="notification-time">
                              {formatTimestamp(notification.created_at)}
                            </div>
                          </div>
                          <button
                            className="notification-item-close"
                            onClick={(e) => handleRemoveNotification(index, e)}
                            aria-label="Remove notification"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="empty-notifications">
                        No notifications
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="navbar-user"
              onClick={toggleUserDropdown}
              ref={userDropdownRef}
            >
              <div className="navbar-user-avatar">
                {profilePhoto ? (
                  <img src={profilePhoto} alt="User avatar" />
                ) : (
                  <svg
                    className="navbar-avatar-placeholder-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-label="User avatar"
                  >
                    <path d="M20 21a8 8 0 0 0-16 0"></path>
                    <circle cx="12" cy="8" r="4"></circle>
                  </svg>
                )}
              </div>
              <div
                className={`navbar-dropdown ${userDropdownOpen ? "open" : ""}`}
              >
                <Link
                  to={`${workspacePrefix}/settings`}
                  className="navbar-dropdown-item"
                  state={{ activeSection: "profile" }}
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Profile
                </Link>
                <Link
                  to={`${workspacePrefix}/settings`}
                  className="navbar-dropdown-item"
                  state={{ activeSection: "account" }}
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-0 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  Account Settings
                </Link>

                <Link
                  to="/dashboard/all-workspaces"
                  className="navbar-dropdown-item"
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="12 3 20 7.5 20 16.5 12 21 4 16.5 4 7.5 12 3"></polyline>
                    <line x1="12" y1="12" x2="20" y2="7.5"></line>
                    <line x1="12" y1="12" x2="12" y2="21"></line>
                    <line x1="12" y1="12" x2="4" y2="7.5"></line>
                  </svg>
                  Workspaces
                </Link>

                <Link
                  to="/app/onboarding?step=2"
                  className="navbar-dropdown-item"
                  onClick={handleGettingStarted}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                    <line x1="4" y1="22" x2="4" y2="15"></line>
                  </svg>
                  Getting Started
                </Link>

                <div className="navbar-dropdown-divider"></div>

                <Link
                  to={`${workspacePrefix}/help`}
                  className="navbar-dropdown-item"
                  state={{ activeSection: "help" }}
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Help & Support
                </Link>
                <div className="navbar-dropdown-divider"></div>
                <Link
                  // to="/"
                  className="navbar-dropdown-item"
                  onClick={handleLogout}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Logout
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      <div
        className={`navbar-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>

      {/* Create Project Modal */}
      {createProjectModalOpen && (
        <div data-rt-modal-overlay className="modal-overlay" onClick={toggleCreateProjectModal}>
          <div
            className="modal create-project-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Create New Project</h2>
              <button
                className="modal-close极"
                onClick={toggleCreateProjectModal}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <form className="create-project-form">
                <div className="form-group">
                  <label htmlFor="project-name">Project Name</label>
                  <input
                    type="text"
                    id="project-name"
                    placeholder="Enter project name"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="form-input"
                    maxLength={30}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="project-description">Description</label>
                  <textarea
                    id="project-description"
                    placeholder="Enter project description"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className="form-input"
                    rows="4"
                  ></textarea>
                </div>
                <div className="form-group">
                  <label htmlFor="project-type">Project Type</label>
                  <select
                    id="project-type"
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Project Type</option>
                    <option value="WEB">Web</option>
                    <option value="MOBILE">Mobile</option>
                    <option value="DESKTOP">Desktop</option>
                    <option value="API_BACKEND">ApiBackend</option>
                    <option value="EMBEDDED">Embedded</option>
                    <option value="CLOUD_DEVOPS">CloudDevOps</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="project-status">Project Status</label>
                  <select
                    id="project-status"
                    value极={projectStatus}
                    onChange={(e) => setProjectStatus(e.target.value)}
                    className="form-input"
                  >
                    <option value="inactive">Inactive</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={toggleCreateProjectModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateProject}
                disabled={loading}
              >
                {loading ? "Creating Project..." : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;